import React, { useState, useEffect } from "react";
import { Edit, Trash, Add } from "iconsax-react";
import { enqueueSnackbar } from "notistack";
import MainButton from "../button/MainButton";
import Loader from "../loading/Loader";
import { useAuth } from "../../contexts/AuthContext";
import { useGlobalInfo } from "../../contexts/GlobalInfoContext";
import { getBrandChart, deleteBrandChart } from "../../utilities/api/sizeChartService";
import UnitToggle from "../shared/UnitToggle";
import EditSizeChartModal from "./EditSizeChartModal";
import { CM_TO_INCH } from "../../contexts/BodyMeasurementServiceContext";
import { extractChartData } from "../../utilities/helpers/sizeChartValidation";

interface SizeChartManagerProps {
  onAddNew: () => void;
  className?: string;
}

const SizeChartManager: React.FC<SizeChartManagerProps> = ({ onAddNew, className = "" }) => {
  const { accessToken } = useAuth();
  const { userInfo } = useGlobalInfo();
  const [brandChartData, setBrandChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedUnit, setSelectedUnit] = useState<"cm" | "inch">("cm");
  const [isDeleting, setIsDeleting] = useState(false);
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    chartData: any;
    chartPath: string[];
    focusSize?: string;
  }>({
    isOpen: false,
    chartData: null,
    chartPath: [],
    focusSize: undefined,
  });

  const fetchBrandChart = React.useCallback(async () => {
    if (!accessToken || !userInfo?.id) return;

    try {
      setLoading(true);
      const response = await getBrandChart(accessToken, String(userInfo.id));

      const chartData = extractChartData(response);
      if (chartData) {
        setBrandChartData(chartData);
      } else {
        setBrandChartData(null);
      }
    } catch (error) {
      console.error("Error fetching brand chart:", error);
      setBrandChartData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, userInfo?.id]);

  useEffect(() => {
    fetchBrandChart();
  }, []);

  const handleDeleteChart = async () => {
    if (!accessToken || !userInfo?.id) return;

    try {
      setIsDeleting(true);
      const response = await deleteBrandChart(accessToken, {
        Brand: String(userInfo.id),
      });

      if (response.ok) {
        enqueueSnackbar("Size chart deleted successfully!", { variant: "success" });
        setBrandChartData(null);
      } else {
        enqueueSnackbar("Failed to delete size chart", { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Error deleting size chart", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditChart = (chartPath: string[], chartData: any, focusSize?: string) => {
    setEditModal({
      isOpen: true,
      chartData,
      chartPath,
      focusSize,
    });
  };

  const handleCloseEditModal = () => {
    setEditModal({
      isOpen: false,
      chartData: null,
      chartPath: [],
      focusSize: undefined,
    });
  };

  const handleSaveEdit = async () => {
    await fetchBrandChart();
  };

  const convertValueForDisplay = (value: number): string => {
    if (selectedUnit === "inch") {
      return (value * CM_TO_INCH).toFixed(1);
    }
    return value.toString();
  };

  const renderSizeTable = (sizes: any[], chartPath: string[]) => {
    if (!sizes || sizes.length === 0) {
      return (
        <div className="p-8 text-center border rounded-lg bg-gorgonzola-blue-50/50 border-gorgonzola-blue-100">
          <p className="text-closed-shutter-200 font-inter-regular">No data available</p>
        </div>
      );
    }

    const measurementKeys = new Set<string>();
    sizes.forEach((size) => {
      Object.keys(size).forEach((key) => {
        if (key !== "Size" && key !== "EuropeSize") {
          measurementKeys.add(key);
        }
      });
    });

    const measurements = Array.from(measurementKeys);

    return (
      <div className="overflow-hidden border rounded-lg border-gorgonzola-blue-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gorgonzola-blue-100">
            <thead className="bg-gorgonzola-blue-50/50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <span className="text-xs font-medium tracking-wider text-closed-shutter-300 font-inter-medium">Size</span>
                </th>
                {measurements.map((measurement) => (
                  <th key={measurement} className="px-4 py-3 text-center">
                    <span className="text-xs font-medium tracking-wider text-closed-shutter-300 font-inter-medium">{measurement}</span>
                  </th>
                ))}
                <th className="px-4 py-3 text-center">
                  <span className="text-xs font-medium tracking-wider text-closed-shutter-300 font-inter-medium">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gorgonzola-blue-100">
              {sizes.map((size, index) => (
                <tr key={index} className="transition-colors hover:bg-gorgonzola-blue-50/50/50">
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <span className="px-2 py-1 text-sm font-medium rounded-md bg-primary-100 text-primary-700 font-inter-medium">{size.Size}</span>
                    </div>
                  </td>
                  {measurements.map((measurement) => (
                    <td key={measurement} className="px-4 py-4 text-center">
                      <span className="text-sm text-closed-shutter-300 font-inter-regular">
                        {typeof size[measurement] === "number" ? `${convertValueForDisplay(size[measurement])} ${selectedUnit}` : size[measurement] || "-"}
                      </span>
                    </td>
                  ))}
                  <td className="px-4 py-4 text-center">
                    <button
                      onClick={() =>
                        handleEditChart(
                          chartPath,
                          sizes.reduce((acc, s) => ({ ...acc, [s.Size]: [s] }), {}),
                          size.Size
                        )
                      }
                      className="inline-flex items-center px-3 py-2 text-xs transition-colors border rounded-md border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300 font-inter-medium"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderChartSection = (data: any, path: string[] = []) => {
    if (!data || typeof data !== "object") return null;

    const flattenData = (obj: any, currentPath: string[] = []): Array<{ path: string[]; data: any[] }> => {
      const results: Array<{ path: string[]; data: any[] }> = [];

      Object.entries(obj).forEach(([key, value]) => {
        const newPath = [...currentPath, key];

        if (Array.isArray(value)) {
          results.push({ path: newPath, data: value });
        } else if (typeof value === "object" && value !== null) {
          results.push(...flattenData(value, newPath));
        }
      });

      return results;
    };

    const flattenedSections = flattenData(data, path);

    return flattenedSections.map(({ path: currentPath, data: sizeData }) => {
      const sectionKey = currentPath.join("-");

      return (
        <div key={sectionKey} className="overflow-hidden bg-white border rounded-lg border-gorgonzola-blue-100">
          <div className="px-4 py-3 border-b border-gorgonzola-blue-100 bg-gorgonzola-blue-50/50">
            <div className="flex items-center gap-2">
              <span className="text-base font-medium text-closed-shutter-300 font-inter-medium">{currentPath.join(" › ")}</span>
              <span className="px-2 py-1 text-xs rounded-full bg-primary-100 text-primary-700 ">{sizeData.length} sizes</span>
            </div>
          </div>
          <div className="p-4">{renderSizeTable(sizeData, currentPath)}</div>
        </div>
      );
    });
  };

  if (loading) {
    return (
      <div className={`flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50 ${className}`}>
        <div className="p-4 md:p-6">
          <h2 className="font-bold text-closed-shutter-300">Size Chart Manager</h2>
        </div>
        <div className="flex items-center justify-center h-64">
          <Loader />
        </div>
      </div>
    );
  }

  if (!brandChartData) {
    return (
      <div className={`flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50 ${className}`}>
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between w-full">
            <h2 className="font-bold text-closed-shutter-300">Size Chart Manager</h2>
          </div>
        </div>

        <div className="flex-1 p-4 md:p-6">
          <div className="flex flex-col items-center justify-center h-64 space-y-4 text-center">
            <div className="space-y-2">
              <h3 className="font-bold text-closed-shutter-300">No Size Charts Found</h3>
              <p className="text-closed-shutter-200">You haven't created any size charts yet. Create your first size chart to get started.</p>
            </div>
            <MainButton onClick={onAddNew} type="primary">
              <Add className="w-4 h-4 mr-2" />
              Create Size Chart
            </MainButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50 ${className}`}>
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between w-full">
          <h2 className="font-bold text-closed-shutter-300">Size Chart Manager</h2>
        </div>
      </div>

      <div className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-4">{renderChartSection(brandChartData)}</div>

        <div className="flex items-center justify-between w-full pt-4 mt-6 border-t border-gorgonzola-blue-50">
          <div className="flex items-center gap-3">
            <MainButton onClick={handleDeleteChart} type="error" disabled={isDeleting} customClass="px-4 py-2 text-xs">
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash className="w-4 h-4 mr-2" />
                  Delete All Size Charts
                </>
              )}
            </MainButton>

            <MainButton onClick={onAddNew} type="primary" customClass="px-3 py-2 text-xs">
              Add New
            </MainButton>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-closed-shutter-300">Unit:</span>
              <UnitToggle selectedUnit={selectedUnit} onUnitChange={setSelectedUnit} />
            </div>
          </div>
        </div>
      </div>

      <EditSizeChartModal
        isOpen={editModal.isOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveEdit}
        chartData={editModal.chartData}
        chartPath={editModal.chartPath}
        fullChartData={brandChartData}
        focusSize={editModal.focusSize}
      />
    </div>
  );
};

export default SizeChartManager;
